import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminInputClass } from '@/components/admin/AdminTableStyles';
import LineItemsEditor, { emptyLine } from '@/components/admin/accounting/LineItemsEditor';
import { accountingError, centsToDollarInput } from '@/lib/accounting';
import { createAccountingInvoice, updateAccountingInvoice, getAccountingCustomer } from '@/lib/api';
import { cn } from '@/lib/utils';
import { localDateInputValue } from '@/lib/dates';

/**
 * One invoice editor, used to raise an invoice and to change one.
 *
 * Create and Edit were never two different forms — they are the same fields
 * writing to the same shape, and the only differences are which endpoint they
 * post to and what the buttons say. Keeping them as one component is what
 * stops "Edit" quietly growing a field that "Create" does not have.
 *
 * Editing preserves the invoice's identity: it PATCHes the same ref and never
 * creates a second record. Revisions preserve earlier contents and saved
 * payments, including after an invoice has been confirmed.
 */

function todayInput() {
  return localDateInputValue();
}

function plusDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return localDateInputValue(date);
}

/** A stored invoice back into the shape the form edits. */
export function formFromInvoice(invoice) {
  return {
    customer_ref: invoice?.customer_ref || '',
    issue_date: invoice?.issue_date || todayInput(),
    due_date: invoice?.due_date || plusDays(30),
    reference: invoice?.reference || '',
    notes: invoice?.notes || '',
    line_items: (invoice?.line_items || []).length
      ? invoice.line_items.map((line) => ({
          item_id: line.item_id || '',
          description: line.description || '',
          quantity: String(line.quantity ?? 1),
          unit_price: centsToDollarInput(line.unit_price_cents),
          tax_ids: line.tax_ids || [],
        }))
      : [emptyLine()],
  };
}

export function blankInvoiceForm(customerRef = '') {
  return {
    customer_ref: customerRef,
    issue_date: todayInput(),
    due_date: plusDays(30),
    reference: '',
    notes: '',
    line_items: [emptyLine()],
  };
}

export default function InvoiceFormDialog({
  open,
  onOpenChange,
  mode = 'create',
  customerRef = '',
  invoice,
  customers,
  items,
  taxes,
  onSaved,
  onSelectExisting,
}) {
  const editing = mode === 'edit';
  const [form, setForm] = useState(() => blankInvoiceForm());
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const requestKey = useRef('');
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [checkingInvoices, setCheckingInvoices] = useState(false);
  const [invoiceCheckError, setInvoiceCheckError] = useState('');
  const [separateCharge, setSeparateCharge] = useState(false);
  const documentInvoice = editing && invoice?.origin === 'application_document';

  useEffect(() => {
    if (!open) return;
    requestKey.current = globalThis.crypto?.randomUUID?.() || `inv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setForm(editing ? formFromInvoice(invoice) : blankInvoiceForm(customerRef));
  }, [open, editing, invoice, customerRef]);

  useEffect(() => {
    if (!open || editing || !form.customer_ref) return;
    let cancelled = false;
    setCheckingInvoices(true); setInvoiceCheckError(''); setExistingInvoices([]); setSeparateCharge(false);
    Promise.resolve().then(() => getAccountingCustomer(form.customer_ref)).then(({ data }) => {
      if (!cancelled) setExistingInvoices((data?.invoices || []).filter((item) => item.status !== 'void'));
    }).catch((error) => {
      if (!cancelled) setInvoiceCheckError(accountingError(error, 'Existing invoices could not be checked. Close and reopen this form to try again.'));
    }).finally(() => { if (!cancelled) setCheckingInvoices(false); });
    return () => { cancelled = true; };
  }, [open, editing, form.customer_ref]);
  const reviewExisting = !editing && existingInvoices.some((item) => item.status === 'draft') && !separateCharge;

  const submit = async () => {
    if (submitting.current || checkingInvoices || invoiceCheckError || reviewExisting) return;
    if (!form.customer_ref) {
      toast.error('Choose the customer this invoice is for.');
      return;
    }
    if (form.line_items.some((line) => !line.description.trim() && !line.item_id)) {
      toast.error('Every line needs a product, a service, or a description.');
      return;
    }
    const payload = {
      customer_ref: form.customer_ref,
      ...(editing ? { expected_updated_at: invoice.updated_at, ...(documentInvoice ? { expected_document_id: invoice.document_id } : {}) } : { idempotency_key: requestKey.current }),
      issue_date: form.issue_date,
      due_date: form.due_date,
      reference: form.reference,
      notes: form.notes,
      line_items: form.line_items.map((line) => ({
        item_id: line.item_id || '',
        description: line.description,
        quantity: Number(line.quantity) || 0,
        unit_price: Number(String(line.unit_price).replace(/[$,]/g, '')) || 0,
        tax_ids: line.tax_ids || [],
      })),
    };
    submitting.current = true;
    setSaving(true);
    try {
      const { data } = editing
        ? await updateAccountingInvoice(invoice.ref, payload)
        : await createAccountingInvoice(payload);
      onOpenChange(false);
      toast.success(
        editing ? `Invoice ${data.number} saved.` : `Invoice ${data.number} created as a draft.`
      );
      await onSaved?.(data);
    } catch (err) {
      toast.error(
        accountingError(
          err,
          editing ? 'The invoice could not be saved.' : 'The invoice could not be created.'
        )
      );
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? `Edit invoice ${invoice?.number || ''}`.trim() : 'Create an invoice'}
          </DialogTitle>
          <DialogDescription>
            {editing
              ? 'This keeps the invoice number and real payments. Earlier contents remain available in version history. Sending a revised invoice is a separate action.'
              : 'Create a separate charge. Review existing invoices below to avoid adding the same charge twice.'}
          </DialogDescription>
        </DialogHeader>
        {!editing && <div className="space-y-2">
          {checkingInvoices && <p className="text-sm">Checking existing invoices…</p>}
          {invoiceCheckError && <p role="alert" className="text-sm text-red-700">{invoiceCheckError}</p>}
          {existingInvoices.length > 0 && <div className="rounded-lg border bg-[#F7F8FA] p-3 text-sm"><p className="font-semibold">Existing invoices for this customer</p><p className="mt-1 text-xs">Use the existing invoice for the same charge. A new invoice adds another charge.</p>{existingInvoices.map((item) => onSelectExisting ? <button type="button" key={item.ref} className="mt-2 block text-left underline" onClick={() => onSelectExisting(item)}>Invoice {item.number} · {item.total_display} · {item.status === 'draft' ? 'Draft' : 'Confirmed'}</button> : <a key={item.ref} className="mt-2 block underline" href={`/admin/accounting/sales/invoices/${encodeURIComponent(item.ref)}`}>Invoice {item.number} · {item.total_display} · {item.status === 'draft' ? 'Draft' : 'Confirmed'} · {item.origin_label}</a>)}{reviewExisting && <Button className="mt-3" variant="outline" onClick={() => setSeparateCharge(true)}>Create additional invoice for a separate charge</Button>}</div>}
        </div>}
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="invoice-form-customer">Customer</Label>
              <select
                id="invoice-form-customer"
                disabled={documentInvoice}
                value={form.customer_ref}
                onChange={(event) => setForm({ ...form, customer_ref: event.target.value })}
                className={cn('mt-1 h-10 w-full rounded-md border bg-white px-3 text-sm', adminInputClass)}
              >
                <option value="">Choose a customer…</option>
                {(customers || []).map((customer) => (
                  <option key={customer.ref} value={customer.ref}>
                    {customer.name} ({customer.source_label})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="invoice-form-reference">Reference</Label>
              <Input
                id="invoice-form-reference"
                disabled={documentInvoice}
                value={form.reference}
                onChange={(event) => setForm({ ...form, reference: event.target.value })}
                placeholder="Optional purchase order or note"
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="invoice-form-date">Invoice date</Label>
              <Input
                id="invoice-form-date"
                type="date"
                value={form.issue_date}
                onChange={(event) => setForm({ ...form, issue_date: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="invoice-form-due">Payment due</Label>
              <Input
                id="invoice-form-due"
                type="date"
                value={form.due_date}
                onChange={(event) => setForm({ ...form, due_date: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
          </div>

          <LineItemsEditor
            lines={form.line_items}
            onChange={(line_items) => setForm({ ...form, line_items })}
            items={documentInvoice ? [] : items}
            taxes={documentInvoice ? [] : taxes}
            disabled={saving}
          />

          <div>
            <Label htmlFor="invoice-form-notes">Notes</Label>
            <Input
              id="invoice-form-notes"
                disabled={documentInvoice}
              value={form.notes}
              onChange={(event) => setForm({ ...form, notes: event.target.value })}
              placeholder="Shown on the invoice"
              className={cn('mt-1', adminInputClass)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={saving || (!editing && (checkingInvoices || Boolean(invoiceCheckError) || reviewExisting))}
            className="bg-[#062B1A] text-white hover:bg-[#041B10]"
          >
            {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
            {editing ? invoice?.status === 'draft' ? 'Save draft' : 'Save & confirm revision' : 'Create draft'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
